import React, { useState, useRef, useEffect } from "react";
import { Bot, Loader2, Send, User } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Ist der Preis marktgerecht?",
  "Welche Daten fehlen noch?",
  "Welche Ausstattung sollten wir hervorheben?",
  "Welche Aufgaben sind vor Veröffentlichung offen?",
  "Was spricht für einen höheren Preis?",
];

export default function AIAssistantPanel({ open, onOpenChange, vehicle }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((ms) => [...ms, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const r = await api.post(`/vehicles/${vehicle.id}/assistant`, { message: msg, history });
      setMessages((ms) => [...ms, { role: "assistant", content: r.data.answer }]);
    } catch (e) {
      toast.error(apiError(e));
      setMessages((ms) => [...ms, { role: "assistant", content: "Der Assistent ist momentan nicht erreichbar. Bitte erneut versuchen." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[440px] sm:max-w-[440px] flex flex-col p-0" data-testid="ai-assistant-panel">
        <SheetHeader className="px-4 py-3 border-b border-border">
          <SheetTitle className="flex items-center gap-2 text-base font-display">
            <Bot className="h-5 w-5 text-primary" /> KI-Assistent
            <span className="text-[10px] font-normal text-muted-foreground">kontextbezogen zu {vehicle?.specs?.marke} {vehicle?.specs?.modell}</span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 scrollbar-thin">
          {messages.length === 0 && (
            <div className="space-y-2 pt-2">
              <p className="text-xs text-muted-foreground">
                Der Assistent analysiert die Fahrzeugakte, macht Vorschläge und beantwortet Fragen.
                Er führt keine Änderungen direkt aus – Sie bestätigen jede Aktion.
              </p>
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid="assistant-suggestion"
                  className="block w-full text-left rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm hover:bg-accent transition-colors">
                  {s}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && <Bot className="h-5 w-5 text-primary shrink-0 mt-1" />}
              <div className={`rounded-xl px-3 py-2 text-sm max-w-[85%] whitespace-pre-line ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary/60 border border-border"}`}>
                {m.content}
              </div>
              {m.role === "user" && <User className="h-5 w-5 text-muted-foreground shrink-0 mt-1" />}
            </div>
          ))}
          {busy && (
            <div className="flex gap-2 items-center text-sm text-muted-foreground">
              <Bot className="h-5 w-5 text-primary" /> <Loader2 className="h-4 w-4 animate-spin" /> analysiert die Fahrzeugakte...
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="border-t border-border p-3 flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Frage zum Fahrzeug stellen..."
            onKeyDown={(e) => e.key === "Enter" && send()} data-testid="assistant-input" />
          <Button onClick={() => send()} disabled={busy || !input.trim()} size="icon" className="shrink-0" data-testid="assistant-send-button">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
